/**
 * H5PEditor
 * H5P content editor with support for direct content or fetch by ID
 * Includes built-in preview functionality
 */

import React, {
    useCallback,
    useMemo,
    useRef,
    forwardRef,
    useImperativeHandle,
    useState,
    useEffect,
    type MouseEvent,
} from 'react';
import { H5PEditorUI, H5PPlayerUI, type H5PPlayerContentData } from '@ai-content-hub/h5p-react';
import type { IEditorModel, IContentMetadata, IPlayerModel } from '@ai-content-hub/h5p-server';
import {
    fetchContent,
    fetchH5PEditorModel,
    fetchH5PPlayerModel,
    extractH5PContentFromData,
    getApiConfig,
    isApiConfigured,
} from '@ai-content-hub/shared-api';
import {
    normalizeStorageMediaUrl,
    resolveH5PParamsMediaUrls,
    resolveH5PParamsMediaUrlsWithOriginals,
    resolveMediaUrlForViewer,
    restoreH5PParamsMediaUrls,
    uploadMedia,
    type H5PMediaOriginals,
} from '@ai-content-hub/content-core';
import type { H5PContentData, H5PContentItem, H5PEditorRef } from '../../types/content';
import { SplitPanel, LoadingSpinner, LoadingOverlay } from '../common';
import { useH5PTranslations } from './i18n';

export interface H5PEditorProps {
    content?: H5PContentItem;
    contentId?: string;
    h5pLibrary?: string;
    showPreview?: boolean;
    readOnly?: boolean;
    onValidationError?: (error: string) => void;
    onLoaded?: () => void;
    onAnswer?: (answer: unknown) => void;
    onSubmit?: (result: unknown) => void;
    onComplete?: (result: unknown) => void;
    onEditorContentChange?: (content: H5PContentItem) => void;
    /**
     * EAA-1029: H5P core's own chrome (Fullscreen, Close, Show advanced, …) is always
     * in English until this is passed — see the matching prop on `H5PPlayer`.
     * This package stays i18n-agnostic on purpose, so the CALLER reads the app's
     * active locale, not this component. Defaults to English.
     */
    language?: string;
}

export const H5PEditor = forwardRef<H5PEditorRef, H5PEditorProps>(
    (
        {
            content: contentProp,
            contentId: contentIdProp,
            h5pLibrary,
            showPreview = false,
            readOnly = false,
            onValidationError,
            onLoaded,
            onEditorContentChange,
            language,
        },
        ref
    ) => {
        const [content, setContent] = useState<H5PContentItem | null>(contentProp || null);
        const [isLoading, setIsLoading] = useState(false);
        const [error, setError] = useState<string | null>(null);

        const t = useH5PTranslations();

        const h5pEditorRef = useRef<InstanceType<typeof H5PEditorUI>>(null);
        // EAA-1857: signed URLs swapped in so the editor can DISPLAY protected media.
        // getEditorContent maps them back before save — persisting a short-lived URL
        // would break the content permanently once it expired.
        const mediaOriginalsRef = useRef<H5PMediaOriginals>({});
        const h5pPlayerRef = useRef(null);

        const [previewContent, setPreviewContent] = useState<H5PContentItem | null>(null);
        const [isEditorLoaded, setIsEditorLoaded] = useState(false);
        const [isPreviewPlayerLoaded, setIsPreviewPlayerLoaded] = useState(false);

        const contentId = content?.id || contentIdProp;

        // Stable editor identity - do NOT use 'preview' (from our own onEditorContentChange sync)
        // so we don't reset isEditorLoaded / remount when preview updates
        const editorContentId =
            contentIdProp ??
            (content?.id && content.id !== 'preview' ? content.id : undefined) ??
            'new';

        // EAA-1857: H5P.getPath prepends the content folder to any path without a
        // scheme, so stored /media/... keys never load. Resolve for display and keep
        // the reverse map so getEditorContent can save the keys back.
        const resolveMediaForDisplay = useCallback(
            (item: H5PContentItem): Promise<H5PContentItem> =>
                resolveH5PParamsMediaUrlsWithOriginals(item.h5p.params).then(
                    ({ params, originals }) => {
                        mediaOriginalsRef.current = originals;
                        return { ...item, h5p: { ...item.h5p, params } };
                    },
                ),
            [],
        );

        /**
         * EAA-1029: media uploads from inside the H5P editor go to ACH, not the H5P
         * server — the last request that still did (`action=files`, which cannot be
         * pre-generated into the static bundle because an upload is not a pure
         * function of anything).
         *
         * The bridge is installed on `window` for `overrides/ach-upload.js` to call
         * from inside the editor's iframe; see that file for why the split. All the
         * judgement lives here:
         *
         *  - `uploadMedia` carries ACH's endpoint, auth and tenant header (EAA-1694).
         *  - What gets PERSISTED is the canonical `/media/{key}` storage form. A
         *    signed URL must never be stored: it expires, so saving one corrupts the
         *    content.
         *  - What gets DISPLAYED is a short-lived signed URL, because the media is
         *    protected. The display→storage pair is registered in the same
         *    originals map EAA-1857 uses, so `getEditorContent` restores the key on
         *    save exactly as it does for media that arrived with the content.
         */
        useEffect(() => {
            const container = window as unknown as {
                __ACH_H5P_UPLOAD__?: (
                    file: File,
                    filename: string,
                ) => Promise<{ path: string; mime: string }>;
            };

            container.__ACH_H5P_UPLOAD__ = async (file, filename) => {
                const result = await uploadMedia(file);
                if (!result.success || !result.url) {
                    throw new Error(result.error || 'Upload failed');
                }

                const storagePath = normalizeStorageMediaUrl(result.url);
                // Protected media needs signing to be viewable; for anything public
                // this returns the path unchanged, so the same code covers both.
                const displayPath = await resolveMediaUrlForViewer(storagePath);
                if (displayPath && displayPath !== storagePath) {
                    mediaOriginalsRef.current = {
                        ...mediaOriginalsRef.current,
                        [displayPath]: storagePath,
                    };
                }

                return {
                    path: displayPath || storagePath,
                    // H5P stores the mime alongside the path; the browser's own type
                    // is authoritative here since ACH does not echo one back.
                    mime: file.type || 'application/octet-stream',
                    name: filename,
                } as { path: string; mime: string };
            };

            return () => {
                delete container.__ACH_H5P_UPLOAD__;
            };
        }, []);

        const getEditorContent = useCallback(async (): Promise<H5PContentData | null> => {
            if (!h5pEditorRef.current) {
                return null;
            }

            try {
                // Brief delay to ensure editor has processed any pending changes
                await new Promise((resolve) => setTimeout(resolve, 50));

                // Get the latest content from editor
                const contentData = await h5pEditorRef.current.getContent();
                if (!contentData) {
                    return null;
                }

                // Verify we got valid content
                if (!contentData.library || !contentData.params) {
                    return null;
                }

                return {
                    library: contentData.library,
                    params: restoreH5PParamsMediaUrls(
                        contentData.params.params || contentData.params,
                        mediaOriginalsRef.current,
                    ),
                    metadata: contentData.params.metadata,
                };
            } catch (err) {
                console.error('Failed to get H5P content:', err);
                return null;
            }
        }, []);

        // EAA-2116: background preview sync must read the form SILENTLY. It used to go
        // through getEditorContent() — the save path — which reports "Tham số bị hỏng"
        // (H5P `errorParamsBroken`) whenever the library form has not finished building:
        // H5P's `getParams()` returns `params: undefined` until `selector.form` exists.
        // The MutationObserver below fires while that form is still being built (always
        // on a rebuilt editor, where `isEditorLoaded` flips as soon as the model resolves),
        // so a race on the network painted the save error under the Title field — and it
        // stayed there after switching to another type (e.g. ACH - Essay). Not-ready is
        // not an error here: return null and let the next mutation sync again. The
        // unvalidated read also stops the sync from marking the form submitted, which
        // painted "required" styling on fields the author had not reached yet.
        const getEditorSnapshot = useCallback((): H5PContentData | null => {
            const snapshot = h5pEditorRef.current?.getContentSnapshot() ?? null;
            const rawParams = snapshot?.params?.params;
            if (!snapshot?.library || rawParams === undefined || rawParams === null) {
                return null;
            }
            return {
                library: snapshot.library,
                // Detach from H5P's live form object, as getContent()'s JSON round-trip did.
                params: restoreH5PParamsMediaUrls(
                    JSON.parse(JSON.stringify(rawParams)),
                    mediaOriginalsRef.current,
                ),
                metadata: snapshot.params.metadata,
            };
        }, []);

        useImperativeHandle(
            ref,
            () => ({
                getContent: getEditorContent,
            }),
            [getEditorContent]
        );

        useEffect(() => {
            let cancelled = false;
            const withResolvedMedia = resolveMediaForDisplay;

            if (contentProp) {
                withResolvedMedia(contentProp)
                    .then((resolved) => {
                        if (!cancelled) setContent(resolved);
                    })
                    .catch(() => {
                        if (!cancelled) setContent(contentProp);
                    });
                return () => {
                    cancelled = true;
                };
            }

            if (contentIdProp && !contentProp) {
                setIsLoading(true);
                setError(null);

                fetchContent(contentIdProp)
                    .then((fetchedContent) => {
                        const h5pData = extractH5PContentFromData(fetchedContent.data);
                        if (!h5pData) {
                            const errorMsg =
                                'This content does not contain H5P data. Use ACHEditor instead.';
                            setError(errorMsg);
                            onValidationError?.(errorMsg);
                            return;
                        }

                        const h5pContent: H5PContentItem = {
                            id: fetchedContent.id,
                            h5pLibrary: fetchedContent.content_type.library,
                            h5p: h5pData,
                        };

                        return withResolvedMedia(h5pContent).then((resolved) => {
                            if (!cancelled) setContent(resolved);
                        });
                    })
                    .catch((err) => {
                        const errorMsg = err.message || 'Failed to load content';
                        setError(errorMsg);
                        onValidationError?.(errorMsg);
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            }

            return () => {
                cancelled = true;
            };
        }, [contentIdProp, contentProp, onValidationError]);

        /**
         * EAA-1029: has ANY editor instance reported loaded on this page yet?
         *
         * `editorloaded` is a one-shot GLOBAL signal in the web component: H5P is
         * initialised behind a `window.h5pIsInitialized` guard, and each instance
         * unregisters its listener after the first fire. A rebuilt instance (an ACH
         * language switch) therefore renders its form correctly but never reports
         * loaded — leaving our overlay up forever over a working editor.
         */
        const hasReportedLoadedRef = useRef(false);

        const handleEditorLoaded = useCallback(() => {
            hasReportedLoadedRef.current = true;
            setIsEditorLoaded(true);
            onLoaded?.();
        }, [onLoaded]);

        const [isPreviewStale, setIsPreviewStale] = useState(false);
        const [isUpdatingPreview, setIsUpdatingPreview] = useState(false);
        const [previewKey, setPreviewKey] = useState(0);
        const [isFullscreen, setIsFullscreen] = useState(false);
        const previewPanelRef = useRef<HTMLDivElement>(null);
        const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
        const previewLoadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

        const updatePreview = useCallback(async () => {
            if (
                !h5pEditorRef.current ||
                isUpdatingPreview ||
                (!showPreview && !onEditorContentChange)
            ) {
                return;
            }

            setIsUpdatingPreview(true);
            try {
                // Brief wait to ensure editor has processed changes
                await new Promise((resolve) => setTimeout(resolve, 80));

                // EAA-2116: silent read — see getEditorSnapshot. Saving still validates.
                const editorContent = getEditorSnapshot();
                if (editorContent && editorContent.library) {
                    // EAA-1029: tear down THIS component's player only when it owns one.
                    // The rAF+setTimeout below exists to let React commit the unmount so the
                    // outgoing player's `disconnectedCallback` runs before a new one mounts —
                    // real work when `showPreview` is true. With `showPreview={false}` (how
                    // ContentDetails uses this component) no player was ever mounted, so this
                    // was ~16ms of waiting for a teardown that could not happen, on the
                    // critical path of every preview refresh. `showPreview` is true for the
                    // embed editors (EmbedEditorPage, H5PEmbedEditorPage), so the wait must be
                    // gated, not deleted.
                    if (showPreview) {
                        setPreviewContent(null);
                        setPreviewKey((prev) => prev + 1);
                        await new Promise<void>((resolve) =>
                            requestAnimationFrame(() => setTimeout(resolve, 0))
                        );
                    }

                    const newPreviewContent: H5PContentItem = {
                        id: contentId || 'preview',
                        h5pLibrary: h5pLibrary || content?.h5pLibrary,
                        h5p: {
                            library: editorContent.library,
                            params: editorContent.params,
                            metadata: editorContent.metadata,
                        },
                    };

                    // EAA-1029: only build this component's OWN preview when it is the one
                    // showing a preview. With `showPreview={false}` the host renders the
                    // player itself (ContentDetails does) and only wants the change
                    // callback — but this used to set `previewContent` anyway, and
                    // SplitPanel keeps its right panel MOUNTED when hidden, so a second
                    // H5P player ran invisibly in the same document. H5P div mode shares
                    // one DOM id space: H5P.AdvancedBlanks binds each blank with
                    // `new Ractive({ el: '#container_cloze<i>' })`, a document-wide
                    // getElementById, so the hidden player (earlier in the DOM) captured
                    // the input and the visible preview rendered the sentence with an
                    // empty span where the blank belongs. Same class of bug as EAA-1929.
                    if (showPreview) {
                        // EAA-1857: getEditorContent hands back storage keys (what must be
                        // SAVED); the preview player needs them signed to render. Resolve for
                        // the preview only — onEditorContentChange keeps the storage keys, or
                        // a consumer saving that payload would persist an expiring URL.
                        const previewParams = await resolveH5PParamsMediaUrls(
                            editorContent.params,
                        ).catch(() => editorContent.params);

                        setPreviewContent({
                            ...newPreviewContent,
                            h5p: { ...newPreviewContent.h5p, params: previewParams },
                        });
                        setIsPreviewStale(false);
                        setIsPreviewPlayerLoaded(false);
                    }
                    onEditorContentChange?.(newPreviewContent);
                }
            } catch (err) {
                console.debug('Preview update failed:', err);
            } finally {
                setIsUpdatingPreview(false);
            }
        }, [
            getEditorSnapshot,
            contentId,
            showPreview,
            isUpdatingPreview,
            h5pLibrary,
            content,
            onEditorContentChange,
        ]);

        const debouncedUpdatePreview = useCallback(() => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            setIsPreviewStale(true);
            // EAA-1029: 250ms, down from 800ms (whose comment claimed 500ms — it had drifted
            // from its own documentation). This timer is the single largest contributor to the
            // "Loading H5P content…" delay: it is spent in full BEFORE the host is even told
            // the content changed, so nothing downstream can overlap it. 250ms still coalesces
            // a burst of typing into one rebuild — the point of the debounce — while removing
            // most of the perceived lag. Chosen with the user (EAA-1029); if a heavy content
            // type turns out to rebuild mid-word, raise it rather than removing it.
            debounceTimerRef.current = setTimeout(() => {
                updatePreview();
            }, 250);
        }, [updatePreview]);

        const handleRefreshPreview = useCallback(
            (e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                updatePreview();
            },
            [updatePreview]
        );

        const handleFullscreenToggle = useCallback(async (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            if (!previewPanelRef.current) return;

            try {
                if (!document.fullscreenElement) {
                    // Enter fullscreen
                    await previewPanelRef.current.requestFullscreen();
                } else {
                    // Exit fullscreen
                    await document.exitFullscreen();
                }
            } catch (err) {
                console.error('Fullscreen error:', err);
            }
        }, []);

        // Listen for fullscreen changes
        useEffect(() => {
            const handleFullscreenChange = () => {
                setIsFullscreen(!!document.fullscreenElement);
            };

            document.addEventListener('fullscreenchange', handleFullscreenChange);
            document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.addEventListener('mozfullscreenchange', handleFullscreenChange);
            document.addEventListener('MSFullscreenChange', handleFullscreenChange);

            return () => {
                document.removeEventListener('fullscreenchange', handleFullscreenChange);
                document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
                document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
                document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
            };
        }, []);

        useEffect(() => {
            if (!showPreview) {
                setPreviewContent(null);
                setIsPreviewStale(false);
                setIsPreviewPlayerLoaded(false);
            }

            if (!showPreview && !onEditorContentChange) {
                return;
            }

            // Cleanup debounce timer when preview is disabled
            return () => {
                if (debounceTimerRef.current) {
                    clearTimeout(debounceTimerRef.current);
                }
            };
        }, [showPreview, onEditorContentChange]);

        useEffect(() => {
            if (!isEditorLoaded || (!showPreview && !onEditorContentChange)) return;

            const editorContainer = document.querySelector('.h5p-editor-panel');
            if (!editorContainer) return;

            // Listen to H5P editor custom events
            const handleH5PChange = () => {
                debouncedUpdatePreview();
            };

            // H5P editor may emit custom events
            editorContainer.addEventListener('h5p-editor-changed', handleH5PChange);
            editorContainer.addEventListener('h5p-content-changed', handleH5PChange);
            editorContainer.addEventListener('h5p-field-changed', handleH5PChange);

            // Use MutationObserver to detect DOM changes in the editor
            // Only trigger on actual content changes, not mouse interactions
            const observer = new MutationObserver((mutations) => {
                // Only update if mutations indicate actual content changes
                const hasContentChange = mutations.some((mutation) => {
                    // Check for attribute changes that indicate content modification
                    if (mutation.type === 'attributes') {
                        const attrName = mutation.attributeName;
                        // Only trigger on value-related attributes, not class/style changes from mouse
                        return (
                            attrName === 'value' ||
                            attrName === 'checked' ||
                            attrName === 'selected'
                        );
                    }
                    // Check for text content or child node changes (actual content edits)
                    if (mutation.type === 'childList' || mutation.type === 'characterData') {
                        return true;
                    }
                    return false;
                });

                if (hasContentChange) {
                    debouncedUpdatePreview();
                }
            });

            // Observe changes in the editor container and its children
            // Only watch for value-related attributes, not class/style changes
            observer.observe(editorContainer, {
                childList: true,
                subtree: true,
                characterData: true,
                attributes: true,
                attributeFilter: ['value', 'checked', 'selected'],
            });

            // Also listen to standard events as fallback
            const handleEditorChange = () => {
                debouncedUpdatePreview();
            };

            editorContainer.addEventListener('change', handleEditorChange, true);
            editorContainer.addEventListener('input', handleEditorChange, true);
            editorContainer.addEventListener('keyup', handleEditorChange, true);
            editorContainer.addEventListener('paste', handleEditorChange, true);

            // Try to access iframe content if available
            const iframe = editorContainer.querySelector('iframe');
            if (iframe) {
                // Wait for iframe to load
                const setupIframeListeners = () => {
                    try {
                        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                        if (iframeDoc && iframeDoc.body) {
                            iframeDoc.addEventListener('change', handleEditorChange, true);
                            iframeDoc.addEventListener('input', handleEditorChange, true);
                            iframeDoc.addEventListener('keyup', handleEditorChange, true);
                            iframeDoc.addEventListener('paste', handleEditorChange, true);

                            const iframeObserver = new MutationObserver(() => {
                                debouncedUpdatePreview();
                            });

                            // Only watch for value-related attributes, not class/style changes from mouse
                            iframeObserver.observe(iframeDoc.body, {
                                childList: true,
                                subtree: true,
                                characterData: true,
                                attributes: true,
                                attributeFilter: ['value', 'checked', 'selected'],
                            });

                            return () => {
                                iframeObserver.disconnect();
                                iframeDoc.removeEventListener('change', handleEditorChange, true);
                                iframeDoc.removeEventListener('input', handleEditorChange, true);
                                iframeDoc.removeEventListener('keyup', handleEditorChange, true);
                                iframeDoc.removeEventListener('paste', handleEditorChange, true);
                            };
                        }
                    } catch (e) {
                        // Cross-origin iframe, can't access content
                        console.debug('Cannot access iframe content (cross-origin):', e);
                    }
                    return null;
                };

                let iframeCleanup: (() => void) | null = null;

                if (iframe.contentDocument) {
                    iframeCleanup = setupIframeListeners();
                } else {
                    iframe.addEventListener('load', () => {
                        iframeCleanup = setupIframeListeners();
                    });
                }

                return () => {
                    observer.disconnect();
                    editorContainer.removeEventListener('h5p-editor-changed', handleH5PChange);
                    editorContainer.removeEventListener('h5p-content-changed', handleH5PChange);
                    editorContainer.removeEventListener('h5p-field-changed', handleH5PChange);
                    editorContainer.removeEventListener('change', handleEditorChange, true);
                    editorContainer.removeEventListener('input', handleEditorChange, true);
                    editorContainer.removeEventListener('keyup', handleEditorChange, true);
                    editorContainer.removeEventListener('paste', handleEditorChange, true);
                    iframeCleanup?.();
                };
            }

            return () => {
                observer.disconnect();
                editorContainer.removeEventListener('h5p-editor-changed', handleH5PChange);
                editorContainer.removeEventListener('h5p-content-changed', handleH5PChange);
                editorContainer.removeEventListener('h5p-field-changed', handleH5PChange);
                editorContainer.removeEventListener('change', handleEditorChange, true);
                editorContainer.removeEventListener('input', handleEditorChange, true);
                editorContainer.removeEventListener('keyup', handleEditorChange, true);
                editorContainer.removeEventListener('paste', handleEditorChange, true);
            };
        }, [showPreview, isEditorLoaded, debouncedUpdatePreview, onEditorContentChange]);

        const loadContentCallback = useCallback(
            async (
                _contentId: string
            ): Promise<
                IEditorModel & { library?: string; metadata?: IContentMetadata; params?: unknown }
            > => {
                if (!isApiConfigured()) {
                    const errorMsg = 'Shared API not configured. Call configureShared() first.';
                    onValidationError?.(errorMsg);
                    throw new Error(errorMsg);
                }

                const config = getApiConfig();
                if (!config.h5pUrl) {
                    const errorMsg = 'H5P server URL not configured.';
                    onValidationError?.(errorMsg);
                    throw new Error(errorMsg);
                }

                const library = h5pLibrary || content?.h5pLibrary;
                let editorModel: IEditorModel;

                try {
                    editorModel = await fetchH5PEditorModel(library, language);
                } catch (err) {
                    const errorMsg =
                        err instanceof Error ? err.message : 'Failed to load H5P editor model';
                    onValidationError?.(errorMsg);
                    throw new Error(errorMsg);
                }

                // EAA-1029: for a REBUILT instance, treat the model resolving as the
                // load signal. H5P's `editorloaded` cannot fire twice on a page (see
                // hasReportedLoadedRef), so the overlay would otherwise never clear.
                // First mount is untouched — it still waits for the real event.
                if (hasReportedLoadedRef.current) {
                    setIsEditorLoaded(true);
                }

                if (content?.h5p) {
                    return {
                        ...editorModel,
                        library: content.h5p.library,
                        params: content.h5p.params,
                        metadata: content.h5p.metadata,
                    };
                }

                return {
                    ...editorModel,
                    library,
                };
            },
            [h5pLibrary, content, onValidationError, language]
        );

        const loadPlayerContentCallback = useCallback(async (): Promise<IPlayerModel> => {
            if (!isApiConfigured()) {
                const errorMsg = 'Shared API not configured. Call configureShared() first.';
                onValidationError?.(errorMsg);
                throw new Error(errorMsg);
            }

            const library = h5pLibrary || content?.h5pLibrary || previewContent?.h5p?.library;

            try {
                return await fetchH5PPlayerModel(library, language);
            } catch (err) {
                const errorMsg =
                    err instanceof Error ? err.message : 'Failed to load H5P player model';
                onValidationError?.(errorMsg);
                throw new Error(errorMsg);
            }
        }, [h5pLibrary, content, previewContent, onValidationError, language]);

        const handleValidationError = useCallback(
            (errorMessage: string) => {
                console.error('H5P validation error:', errorMessage);
                onValidationError?.(errorMessage);
            },
            [onValidationError]
        );

        const playerContentData: H5PPlayerContentData | undefined = useMemo(() => {
            if (!previewContent?.h5p) {
                return undefined;
            }
            // Create a new object each time to ensure React detects changes
            return {
                library: previewContent.h5p.library,
                params: previewContent.h5p.params,
                metadata: previewContent.h5p.metadata,
            };
        }, [previewContent]);

        const onPlayerInitialized = useCallback((initializedContentId: string) => {
            if (previewLoadTimeoutRef.current) {
                clearTimeout(previewLoadTimeoutRef.current);
                previewLoadTimeoutRef.current = null;
            }
            setIsPreviewPlayerLoaded(true);
            console.log('H5P preview player initialized:', initializedContentId);
        }, []);

        useEffect(() => {
            if (previewContent?.h5p) {
                previewLoadTimeoutRef.current = setTimeout(() => {
                    setIsPreviewPlayerLoaded(true);
                }, 1000);
            }
            return () => {
                if (previewLoadTimeoutRef.current) {
                    clearTimeout(previewLoadTimeoutRef.current);
                    previewLoadTimeoutRef.current = null;
                }
            };
        }, [previewContent?.h5p]);

        const onxAPIStatement = useCallback(() => {
            // Preview mode - ignore xAPI statements
        }, []);

        const library = h5pLibrary || content?.h5pLibrary;

        useEffect(() => {
            setIsEditorLoaded(false);
        }, [library, editorContentId]);

        /**
         * EAA-1029: apply an ACH language switch to a live editor.
         *
         * H5P builds every label from the semantics it fetched AT CONSTRUCTION, so
         * there is nothing to re-render — swapping language means rebuilding the
         * editor instance, which `languageKey` in its key does.
         *
         * A naive rebuild would silently discard whatever the author has typed, so
         * the current form is read back first and becomes the rebuilt editor's
         * starting content. It is re-resolved for display on the way in because
         * getEditorContent hands back storage KEYS (EAA-1857), which would render
         * as broken media otherwise.
         */
        const [languageKey, setLanguageKey] = useState(language);
        const appliedLanguageRef = useRef(language);

        useEffect(() => {
            if (appliedLanguageRef.current === language) {
                return;
            }
            appliedLanguageRef.current = language;
            let cancelled = false;

            const carryWorkAcrossRebuild = async () => {
                // getEditorContent() is the SAVE path: it returns null when params
                // fail validation and — silently — when the main title is unset. A
                // half-filled form is exactly what has to survive here, so read the
                // unvalidated snapshot instead. (This was the bug: switching language
                // on a form with an empty Title carried nothing at all.)
                const inProgress = isEditorLoaded
                    ? (h5pEditorRef.current?.getContentSnapshot?.() ?? null)
                    : null;
                if (cancelled) {
                    return;
                }
                if (inProgress) {
                    const carried: H5PContentItem = {
                        // 'preview' is what this component's own change sync uses, and
                        // editorContentId deliberately ignores it, so the editor's
                        // identity stays stable across the rebuild.
                        id: content?.id ?? contentIdProp ?? 'preview',
                        h5pLibrary: inProgress.library || content?.h5pLibrary || h5pLibrary || '',
                        // The snapshot arrives in h5p-react's nested shape
                        // (`params: { params, metadata }`); flatten it the same way
                        // getEditorContent does for this package's own type.
                        h5p: {
                            library: inProgress.library,
                            params: inProgress.params.params,
                            metadata: inProgress.params.metadata,
                        },
                    };
                    const resolved = await resolveMediaForDisplay(carried);
                    if (cancelled) {
                        return;
                    }
                    setContent(resolved);
                }
                setLanguageKey(language);
            };

            void carryWorkAcrossRebuild();
            return () => {
                cancelled = true;
            };
        }, [
            language,
            isEditorLoaded,
            resolveMediaForDisplay,
            content,
            contentIdProp,
            h5pLibrary,
        ]);

        const editorUI = useMemo(
            () => (
                <H5PEditorUI
                    key={`${library || 'default'}-${editorContentId}-${languageKey ?? 'en'}`}
                    ref={h5pEditorRef}
                    library={library}
                    contentId={editorContentId}
                    loadContentCallback={loadContentCallback}
                    onLoaded={handleEditorLoaded}
                    onValidationError={handleValidationError}
                />
            ),
            [
                library,
                editorContentId,
                languageKey,
                loadContentCallback,
                handleEditorLoaded,
                handleValidationError,
            ]
        );

        if (isLoading) {
            return (
                <div
                    className="h5p-editor-loading"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 200,
                        padding: 24,
                    }}
                >
                    <LoadingSpinner message={t.loading} />
                </div>
            );
        }

        if (error) {
            return (
                <div
                    className="h5p-editor-error"
                    style={{ padding: '24px', color: 'hsl(var(--destructive, 347 79% 46%))', textAlign: 'center' }}
                >
                    {error}
                </div>
            );
        }

        const playerContentId = previewContent?.id || contentId || 'preview';

        // Editor content (left panel)
        const editorPanelContent = (
            <div
                className="h5p-editor-panel"
                style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    minHeight: 200,
                }}
            >
                {editorUI}
                {!isEditorLoaded && (
                    <LoadingOverlay message={t.loading} />
                )}
                {readOnly && (
                    <div
                        className="h5p-editor-readonly-overlay"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'transparent',
                            pointerEvents: 'auto',
                            zIndex: 10,
                        }}
                    />
                )}
            </div>
        );

        // Preview panel header button styles
        const previewHeaderButtonStyle: React.CSSProperties = {
            padding: '6px 12px',
            fontSize: 12,
            cursor: 'pointer',
            border: '1px solid #d1d5db',
            borderRadius: 4,
            background: '#fff',
            color: '#374151',
            fontWeight: 500,
        };

        // Preview content (right panel)
        const previewPanelContent = (
            <div
                ref={previewPanelRef}
                className="h5p-preview-panel"
                style={{
                    height: '100%',
                    background: isFullscreen ? '#fafafa' : '#f9fafb',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <div
                    className="h5p-preview-panel-header"
                    style={{
                        padding: '10px 16px',
                        borderBottom: '1px solid #e5e7eb',
                        fontWeight: 600,
                        fontSize: 14,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'white',
                        flexShrink: 0,
                    }}
                >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {t.preview}
                        {isPreviewStale && (
                            <span
                                style={{
                                    fontSize: 12,
                                    color: '#fa8c16',
                                    fontWeight: 400,
                                }}
                            >
                                {t.previewUpdating}
                            </span>
                        )}
                    </span>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button
                            type="button"
                            onClick={handleFullscreenToggle}
                            style={previewHeaderButtonStyle}
                            title={isFullscreen ? t.exitFullscreen : t.fullscreen}
                        >
                            {isFullscreen ? `⤓ ${t.exitFullscreen}` : `⤢ ${t.fullscreen}`}
                        </button>
                        <button
                            type="button"
                            onClick={handleRefreshPreview}
                            disabled={isUpdatingPreview}
                            style={{
                                ...previewHeaderButtonStyle,
                                cursor: isUpdatingPreview ? 'not-allowed' : 'pointer',
                                background: isUpdatingPreview ? '#f3f4f6' : '#fff',
                                color: isUpdatingPreview ? '#9ca3af' : '#374151',
                            }}
                            title={t.refresh}
                        >
                            {isUpdatingPreview ? t.refreshing : t.refresh}
                        </button>
                    </div>
                </div>
                <div
                    className="h5p-preview-panel-content"
                    style={{
                        flex: 1,
                        padding: '16px',
                        overflow: 'auto',
                        background: isFullscreen ? '#fafafa' : 'transparent',
                        minHeight: isFullscreen ? '100vh' : 'auto',
                    }}
                >
                    {/* EAA-1029: `showPreview` gates the PLAYER, not just the panel —
                        SplitPanel keeps the hidden right panel mounted, so without this a
                        stale `previewContent` would still run a second H5P instance in the
                        host's document and steal the visible preview's blank inputs. */}
                    {showPreview && previewContent?.h5p ? (
                        <div
                            style={{
                                position: 'relative',
                                width: '100%',
                                minHeight: 200,
                                flex: 1,
                            }}
                        >
                            <H5PPlayerUI
                                key={`preview-${playerContentId}-${previewKey}`}
                                ref={h5pPlayerRef}
                                contentId={playerContentId}
                                contentData={playerContentData}
                                readOnlyState={true}
                                loadContentCallback={loadPlayerContentCallback}
                                onInitialized={onPlayerInitialized}
                                onxAPIStatement={onxAPIStatement}
                            />
                            {!isPreviewPlayerLoaded && (
                                <LoadingOverlay
                                    message={t.loading}
                                    background="rgba(249, 250, 251, 0.9)"
                                />
                            )}
                        </div>
                    ) : (
                        <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
                            {t.previewPlaceholder}
                        </div>
                    )}
                </div>
            </div>
        );

        return (
            <SplitPanel
                leftContent={editorPanelContent}
                rightContent={previewPanelContent}
                showRight={showPreview}
                classPrefix="h5p-editor"
                className="h5p-editor-container"
            />
        );
    }
);

H5PEditor.displayName = 'H5PEditor';

export default H5PEditor;
